import { floatVal, intVal, Nullable } from "@jamesgmarks/utilities";
import {
  Grid,
  Paper,
  Table as MuiTable,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Button,
  Chip,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  ListItemText,
  TableSortLabel,
  Tooltip,
  Fab,
  Stack,
} from "@mui/material";
import { useEffect, useMemo, useState, useCallback } from "react";
import {
  IQueryCommandOptions,
  Equal,
  GreaterThanOrEqual,
  LessThanOrEqual,
  In,
  Like,
  TOrderDirection,
} from '@llws/dynamic-router-utils';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import LinkIcon from '@mui/icons-material/Link';
import ClearIcon from '@mui/icons-material/Clear';
import { IClientDropdownItem } from "../../../entity-interfaces/IClientDropdownItem";
import { getMany } from "../../../redux/features/dynamic/actions";
import { useAppSelector } from "../../../redux/hooks";
import { ClientDropdown } from "../../parts/ClientDropdown";
import { StyledTableCell, StyledTableRow } from "../../parts/mui/StyledTables";
import { Spinner } from "../../parts/Spinner";
import { getDiscountRateFromSubscription } from "src/app-utils";
import { rowsPerPageList } from "../Clients/Tabs";
import { ISubscriptions } from "@llws/lift-entity-interfaces";

const relationsQueryCommand: IQueryCommandOptions = {
  alias: 's',
  relations: [
    { path: 's.client', alias: 'c' },
    { path: 's.baseSubscription', alias: 'bs' },
    { path: 's.subscriptionsHasBuildings', alias: 'shb' },
    { path: 'bs.service', alias: 'svc' },
    { path: 'bs.partner', alias: 'p' },
    { path: 's.billingFrequency', alias: 'fq' },
    { path: 'svc.billingType', alias: 'bt' },
    { path: 's.salesRep', alias: 'sr' },
    { path: 'sr.user', alias: 'u' },
  ],
};

export const SubscriptionListRow = ({ subscription }: { subscription: ISubscriptions; }) => {
  const baseSubscription = subscription.baseSubscription;
  if (!baseSubscription) {
    console.warn('Base SUBSCRIPTION', { subscription });
    return <div>No base subscription</div>;
  }
  const service = baseSubscription.service;
  if (!service) {
    console.warn({ service });
    return <div>No service for base subscription</div>;
  }

  // Calculate subscription status based on expiry date
  const getSubscriptionStatus = () => {
    if (!subscription.expiryDate) {
      return 'Active';
    }

    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(now.getDate() + 1);
    const expiryDate = new Date(subscription.expiryDate);

    const isAfterCurrentMonth = () => {
      const nextMonthStart = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      return expiryDate >= nextMonthStart;
    };

    const isInCurrentMonth = () => {
      const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const nextMonthStart = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      return expiryDate >= currentMonthStart && expiryDate < nextMonthStart;
    };

    const isBeforeTomorrow = () => expiryDate < tomorrow;

    if (isAfterCurrentMonth()) return 'Active';
    if (isInCurrentMonth()) {
      return isBeforeTomorrow() ? 'Expired This Month' : 'Expiring This Month';
    }
    return 'Expired';
  };

  const status = getSubscriptionStatus();

  const statusColorMap: Record<string, string> = {
    'Active': '#4caf50',
    'Expiring This Month': '#ff9800',
    'Expired This Month': '#f44336',
    'Expired': '#9e9e9e',
  };

  const getStatusColor = (status: string) => statusColorMap[status] ?? '#ffffff';

  return (
    <StyledTableRow
      customSx={{ userSelect: 'none' }}
      dark={true}
      hover={true}
    >
      <StyledTableCell
        dark={true}
        style={{ textAlign: 'center' }}
      >
        {subscription.id}
      </StyledTableCell>
      <StyledTableCell dark={true} style={{ textAlign: 'center' }}>
        {subscription.client?.name}
      </StyledTableCell>
      <StyledTableCell dark={true} style={{ textAlign: 'left' }}>
        {service.invoiceItem ?? baseSubscription.partner?.name}
      </StyledTableCell>
      <StyledTableCell dark={true} style={{ textAlign: 'left' }}>
        {subscription.invoiceDescription}
      </StyledTableCell>
      <StyledTableCell dark={true} style={{ textAlign: 'center' }}>
        {subscription.billingFrequency?.name} - {service.billingType?.name ?? 'N/A'}
      </StyledTableCell>
      <StyledTableCell dark={true} style={{ textAlign: 'center' }}>
        {subscription.basePrice}
      </StyledTableCell>
      <StyledTableCell dark={true} style={{ textAlign: 'center' }}>
        {`${floatVal(getDiscountRateFromSubscription(subscription)) * 100}%`}
      </StyledTableCell>
      <StyledTableCell dark={true} style={{ textAlign: 'center' }}>
        {subscription.startDate}
      </StyledTableCell>
      <StyledTableCell dark={true} style={{ textAlign: 'center' }}>
        {subscription.expiryDate ?? '------'}
      </StyledTableCell>
      <StyledTableCell dark={true} style={{ textAlign: 'center' }}>
        <span style={{ color: getStatusColor(status), fontWeight: 'bold' }}>
          {status}
        </span>
      </StyledTableCell>
      <StyledTableCell dark={true} style={{ textAlign: 'center' }}>
        {(
          subscription.rateExpiryDate == null
          || subscription.rateExpiryDate.toString() === '0000-00-00'
          || subscription.rateExpiryDate.toString() === '1899-11-30'
        ) ? '------' : subscription.rateExpiryDate.toString()
        }
      </StyledTableCell>
      <StyledTableCell dark={true} style={{ textAlign: 'center' }}>
        {subscription.salesRep?.user?.firstName} {subscription.salesRep?.user?.lastName}
      </StyledTableCell>

      <StyledTableCell dark={true} style={{ textAlign: 'center' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Button
            variant="outlined"
            size="small"
            sx={{
              color: '#ffffff',
              borderColor: '#ffffff',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderColor: '#ffffff',
              },
            }}
            onClick={() => window.open(
              `https://superlift.theliftsystem.com/subscriptions/view/${subscription.id}`,
              '_blank',
            )}
          >
            View
          </Button>
          <Button
            variant="outlined"
            size="small"
            sx={{
              color: '#ffffff',
              borderColor: '#ffffff',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderColor: '#ffffff',
              },
            }}
            onClick={() => window.open(
              `/subscriptions/form/${subscription.id}`,
              '_blank',
            )}
          >
            Try New Editor
          </Button>
        </Box>
      </StyledTableCell>
    </StyledTableRow>
  );
};

export const SubscriptionList = () => {

  const loadedState = useAppSelector((state) => state.dynamic.data.subscriptions?.loadedState);
  const subscriptions = useAppSelector((state) => state.dynamic.data.subscriptions?.list);
  const subscriptionMetadata = useAppSelector((state) => state.dynamic.metaData);
  const salesReps = useAppSelector((state) => state.dynamic.data.sales_reps?.list ?? []);

  // Search and filter states
  const [searchClient, setSearchClient] = useState<Nullable<IClientDropdownItem>>(null);
  const [searchField, setSearchField] = useState<Nullable<string>>(null);
  const [debouncedSearchField, setDebouncedSearchField] = useState<Nullable<string>>(null);
  const [salesRepFilter, setSalesRepFilter] = useState<string[]>([]);
  const [dateRangeFilter, setDateRangeFilter] = useState({
    startDateFrom: '',
    startDateTo: '',
    expiryDateFrom: '',
    expiryDateTo: '',
  });

  const [showSearch] = useState<boolean>(true);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0); // 0-based for Material-UI pagination
  const [limit, setLimit] = useState<number>(20);
  const [sortOrder, setSortOrder] = useState<TOrderDirection>('asc');
  const [sortBy, setSortBy] = useState<string>('id');

  const getTableSortDirection = (field: string): 'asc' | 'desc' | undefined => {
    if (sortBy !== field) return 'asc';
    if (sortOrder === 'asc') return 'asc';
    if (sortOrder === 'desc') return 'desc';
    return undefined; // need this undefined because of the typing difference between material ui and DR sort direction
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchField(searchField);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchField]);

  const resetPageOnFilterChange = useCallback(() => {
    setPage(0);
  }, []);

  // Handle sorting
  const handleSort = useCallback((field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPage(0);
  }, [sortBy, sortOrder]);

  useEffect(resetPageOnFilterChange, [
    searchClient,
    debouncedSearchField,
    salesRepFilter,
    dateRangeFilter,
    resetPageOnFilterChange,
  ]);

  const clearAllFilters = useCallback(() => {
    setSearchClient(null);
    setSearchField(null);
    setDebouncedSearchField(null);
    setSalesRepFilter([]);
    setDateRangeFilter({
      startDateFrom: '',
      startDateTo: '',
      expiryDateFrom: '',
      expiryDateTo: '',
    });
    setPage(1);
  }, []);

  // Count active filters
  const activeFiltersCount = useMemo(() => {
    return [
      ...(searchClient ? ['client'] : []),
      ...(debouncedSearchField ? ['description'] : []),
      ...(salesRepFilter.length > 0 ? ['salesRep'] : []),
      ...(dateRangeFilter.startDateFrom || dateRangeFilter.startDateTo ? ['startDate'] : []),
      ...(dateRangeFilter.expiryDateFrom || dateRangeFilter.expiryDateTo ? ['expiryDate'] : []),
    ].length;
  }, [searchClient, debouncedSearchField, salesRepFilter, dateRangeFilter]);

  useEffect(() => { getMany('subscriptions', relationsQueryCommand); }, []);

  // Load sales reps for filter
  useEffect(() => {
    if (salesReps.length === 0) {
      getMany('sales_reps', {
        alias: 'sr',
        relations: [{ path: 'sr.user', alias: 'u' }],
        where: [Equal('status', 'active')],
      });
    }
  }, [salesReps.length]);

  // Build search and filter query
  const searchAndFilters: IQueryCommandOptions = useMemo(() => {
    const filterConditions = [
      // Client filter
      ...(searchClient ? [Equal('clientId', searchClient.id)] : []),

      // Sales rep filter
      ...(salesRepFilter.length > 0 ? [In('salesRepId', salesRepFilter.map(id => intVal(id)))] : []),

      // Date range filters
      ...(dateRangeFilter.startDateFrom ? [GreaterThanOrEqual('startDate', dateRangeFilter.startDateFrom)] : []),
      ...(dateRangeFilter.startDateTo ? [LessThanOrEqual('startDate', dateRangeFilter.startDateTo)] : []),
      ...(dateRangeFilter.expiryDateFrom ? [GreaterThanOrEqual('expiryDate', dateRangeFilter.expiryDateFrom)] : []),
      ...(dateRangeFilter.expiryDateTo ? [LessThanOrEqual('expiryDate', dateRangeFilter.expiryDateTo)] : []),

      // Description search
      ...(debouncedSearchField ? [Like('invoiceDescription', `%${debouncedSearchField}%`)] : []),
    ];

    return {
      ...relationsQueryCommand,
      where: filterConditions,
      orderBy: [{ field: sortBy, direction: sortOrder ?? 'asc' }],
      page: page + 1,
      limit,
    };
  }, [searchClient, debouncedSearchField, salesRepFilter, dateRangeFilter, sortBy, sortOrder, page, limit]);

  useEffect(() => { getMany('subscriptions', searchAndFilters); }, [searchAndFilters]);

  // Pagination rows handler
  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setLimit(intVal(event.target.value));
    setPage(0);
  };

  return (
    <>
      <header className="Section-header">Subscription List</header>
      <Grid container spacing={2} mt={2} mb={2} justifyContent="center" alignItems="center">
        <Grid item xs={12}>
          {showSearch && (
            <Paper variant='outlined' sx={{ padding: '1rem', backgroundColor: '#e0e0e0' }}>
              {/* Basic Search */}
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={3}>
                  <Typography variant="subtitle2" gutterBottom>Client:</Typography>
                  <ClientDropdown
                    clientId={searchClient?.id ? intVal(searchClient.id) : null}
                    onClientChanged={(client) => { setSearchClient(client); }}
                    onClientFreeForm={() => { }}
                    key={searchClient?.id ?? 'no-client'}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="subtitle2" gutterBottom>Description:</Typography>
                  <TextField
                    placeholder="Search by description..."
                    type="text"
                    sx={{ background: "white" }}
                    size="small"
                    fullWidth={true}
                    value={searchField ?? ''}
                    onChange={(e) => setSearchField(e.target.value || null)}
                    helperText={searchField && searchField !== debouncedSearchField ? "Searching..." : ""}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="subtitle2" gutterBottom>Sales Rep:</Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      multiple
                      value={salesRepFilter}
                      onChange={(e) => setSalesRepFilter(
                        typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value,
                      )}
                      sx={{ background: "white" }}
                      displayEmpty
                      renderValue={(selected) => {
                        const handlers = [
                          () => <em>All Sales Reps</em>,
                          () => {
                            const rep = salesReps.find(sr => sr.id?.toString() === selected[0]);
                            return `${rep?.user?.firstName ?? 'Unknown'} ${rep?.user?.lastName ?? ''}`.trim();
                          },
                          () => `${selected.length} selected`,
                        ];

                        const handlerIndex = Math.min(selected.length, 2);
                        return handlers[handlerIndex]();
                      }}
                    >
                      {salesReps.filter(sr => sr.id).map((salesRep) => (
                        <MenuItem key={salesRep.id} value={salesRep.id!.toString()}>
                          <Checkbox checked={salesRepFilter.indexOf(salesRep.id!.toString()) > -1} />
                          <ListItemText primary={`${salesRep.user?.firstName} ${salesRep.user?.lastName}`} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                      startIcon={<ExpandMoreIcon />}
                    >
                      {showAdvancedFilters ? 'Hide' : 'Show'} Filters
                    </Button>
                    {activeFiltersCount > 0 && (
                      <Button
                        variant="text"
                        size="small"
                        onClick={clearAllFilters}
                        startIcon={<ClearIcon />}
                        color="error"
                      >
                        Clear ({activeFiltersCount})
                      </Button>
                    )}
                  </Box>
                </Grid>
              </Grid>

              {/* Advanced Filters */}
              {showAdvancedFilters && (
                <Accordion
                  expanded={showAdvancedFilters}
                  sx={{
                    mt: 2,
                    backgroundColor: '#f0f7ff',
                    boxShadow: 'none',
                    border: '1px solid #b3d9ff',
                    borderRadius: '8px',
                    '&.MuiAccordion-root': {
                      backgroundColor: '#f0f7ff',
                    },
                    '& .MuiAccordionDetails-root': {
                      backgroundColor: '#f0f7ff',
                      padding: '16px',
                    },
                  }}
                >
                  <AccordionSummary sx={{ padding: '8px 16px', minHeight: 'auto' }}>
                    <Typography variant="subtitle1">Date Range Filters</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={3}>
                        <Typography variant="subtitle2" gutterBottom>Start Date From:</Typography>
                        <TextField
                          type="date"
                          size="small"
                          fullWidth
                          value={dateRangeFilter.startDateFrom}
                          onChange={(e) => setDateRangeFilter(prev => ({
                            ...prev,
                            startDateFrom: e.target.value,
                          }))}
                          sx={{ background: "white" }}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="subtitle2" gutterBottom>Start Date To:</Typography>
                        <TextField
                          type="date"
                          size="small"
                          fullWidth
                          value={dateRangeFilter.startDateTo}
                          onChange={(e) => setDateRangeFilter(prev => ({
                            ...prev,
                            startDateTo: e.target.value,
                          }))}
                          sx={{ background: "white" }}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="subtitle2" gutterBottom>Expiry Date From:</Typography>
                        <TextField
                          type="date"
                          size="small"
                          fullWidth
                          value={dateRangeFilter.expiryDateFrom}
                          onChange={(e) => setDateRangeFilter(prev => ({
                            ...prev,
                            expiryDateFrom: e.target.value,
                          }))}
                          sx={{ background: "white" }}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="subtitle2" gutterBottom>Expiry Date To:</Typography>
                        <TextField
                          type="date"
                          size="small"
                          fullWidth
                          value={dateRangeFilter.expiryDateTo}
                          onChange={(e) => setDateRangeFilter(prev => ({
                            ...prev,
                            expiryDateTo: e.target.value,
                          }))}
                          sx={{ background: "white" }}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Active Filters Display */}
              {activeFiltersCount > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Active Filters:</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {searchClient && (
                      <Chip
                        label={`Client: ${searchClient.name}`}
                        onDelete={() => setSearchClient(null)}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    )}
                    {debouncedSearchField && (
                      <Chip
                        label={`Description: "${debouncedSearchField}"`}
                        onDelete={() => {
                          setSearchField(null);
                          setDebouncedSearchField(null);
                        }}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    )}
                    {salesRepFilter.length > 0 && (
                      <Chip
                        label={
                          `Sales Rep: ${salesRepFilter.length === 1
                            ? (() => {
                              const rep = salesReps.find(sr => sr.id?.toString() === salesRepFilter[0]);
                              return `${rep?.user?.firstName ?? 'Unknown'} ${rep?.user?.lastName ?? ''}`.trim();
                            })()
                            : `${salesRepFilter.length} selected`
                          }`
                        }
                        onDelete={() => setSalesRepFilter([])}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    )}
                    {(dateRangeFilter.startDateFrom || dateRangeFilter.startDateTo) && (
                      <Chip
                        label={
                          `Start Date: `
                          + `${dateRangeFilter.startDateFrom ?? '...'} - ${dateRangeFilter.startDateTo ?? '...'
                          }`}
                        onDelete={() => setDateRangeFilter(prev => ({
                          ...prev,
                          startDateFrom: '',
                          startDateTo: '',
                        }))}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    )}
                    {(dateRangeFilter.expiryDateFrom || dateRangeFilter.expiryDateTo) && (
                      <Chip
                        label={
                          `Expiry Date: `
                          + `${dateRangeFilter.expiryDateFrom || '...'} - ${dateRangeFilter.expiryDateTo || '...'
                          }`}
                        onDelete={() => setDateRangeFilter(prev => ({
                          ...prev,
                          expiryDateFrom: '',
                          expiryDateTo: '',
                        }))}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </Box>
              )}
            </Paper>
          )}
        </Grid>
      </Grid>

      {loadedState === 'loading' && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <Spinner state={loadedState} />
        </Box>
      )}
      {loadedState === 'loaded' && (
        <>
          {/* Search Results Summary */}
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {subscriptionMetadata?.totalRecords === 0 ? (
                'No subscriptions found'
              ) : (
                `Showing ${(page * limit) + 1}-${Math.min((page + 1) * limit, subscriptionMetadata?.totalRecords ?? 0)
                } of ${subscriptionMetadata?.totalRecords ?? 0} subscriptions`
              )}
              {activeFiltersCount > 0 && (
                ` (${activeFiltersCount} filter${activeFiltersCount > 1 ? 's' : ''} applied)`
              )}
            </Typography>
            {(searchField && searchField !== debouncedSearchField) && (
              <Typography variant="caption" color="primary">
                Searching...
              </Typography>
            )}
          </Box>
          <TablePagination
            component="div"
            sx={{
              '& p': { marginTop: '1rem' },
              position: 'sticky',
              top: 0,
              zIndex: 50,
              backgroundColor: 'white',
              borderBottom: '1px solid #e0e0e0',
            }}
            count={subscriptionMetadata?.totalRecords ?? 0}
            page={page}
            rowsPerPage={limit}
            showFirstButton
            showLastButton
            rowsPerPageOptions={rowsPerPageList}
            onPageChange={(e, newPage) => { setPage(newPage); }}
            onRowsPerPageChange={handleRowsPerPageChange}
          />

          <TableContainer
            component={Paper}
            elevation={12}
            sx={{
              mb: 1,
              maxHeight: '65vh',
            }}
          >

            {(subscriptions ?? []).length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary">
                  No subscriptions found
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {activeFiltersCount > 0
                    ? 'Try adjusting your search criteria or clearing filters.'
                    : 'There are no subscriptions to display.'
                  }
                </Typography>
                {activeFiltersCount > 0 && (
                  <Button
                    variant="outlined"
                    onClick={clearAllFilters}
                    sx={{ mt: 2 }}
                    startIcon={<ClearIcon />}
                  >
                    Clear All Filters
                  </Button>
                )}
              </Box>
            ) : (
              <MuiTable
                stickyHeader
                style={{ borderCollapse: 'collapse' }}
              >
                <TableHead>
                  <StyledTableRow dark={true}>
                    <StyledTableCell
                      dark={true}
                      style={{ textAlign: 'center' }}
                    >
                      <TableSortLabel
                        active={sortBy === 'id'}
                        direction={getTableSortDirection('id')}
                        onClick={() => handleSort('id')}
                        sx={{
                          color: 'inherit !important',
                          '&:hover': { color: 'inherit !important' },
                          '&.Mui-active': { color: 'inherit !important' },
                          '& .MuiTableSortLabel-icon': { color: 'inherit !important' },
                        }}
                      >
                        ID
                      </TableSortLabel>
                    </StyledTableCell>

                    <StyledTableCell dark={true}>
                      <TableSortLabel
                        active={sortBy === 'c.name'}
                        direction={getTableSortDirection('c.name')}
                        onClick={() => handleSort('c.name')}
                        sx={{
                          color: 'inherit !important',
                          '&:hover': { color: 'inherit !important' },
                          '&.Mui-active': { color: 'inherit !important' },
                          '& .MuiTableSortLabel-icon': { color: 'inherit !important' },
                        }}
                      >
                        Client
                      </TableSortLabel>
                    </StyledTableCell>
                    <StyledTableCell dark={true}>
                      <TableSortLabel
                        active={sortBy === 'svc.invoiceItem'}
                        direction={getTableSortDirection('svc.invoiceItem')}
                        onClick={() => handleSort('svc.invoiceItem')}
                        sx={{
                          color: 'inherit !important',
                          '&:hover': { color: 'inherit !important' },
                          '&.Mui-active': { color: 'inherit !important' },
                          '& .MuiTableSortLabel-icon': { color: 'inherit !important' },
                        }}
                      >
                        Item
                      </TableSortLabel>
                    </StyledTableCell>
                    <StyledTableCell dark={true}>
                      <TableSortLabel
                        active={sortBy === 'invoiceDescription'}
                        direction={getTableSortDirection('invoiceDescription')}
                        onClick={() => handleSort('invoiceDescription')}
                        sx={{
                          color: 'inherit !important',
                          '&:hover': { color: 'inherit !important' },
                          '&.Mui-active': { color: 'inherit !important' },
                          '& .MuiTableSortLabel-icon': { color: 'inherit !important' },
                        }}
                      >
                        Description
                      </TableSortLabel>
                    </StyledTableCell>
                    <StyledTableCell dark={true}>
                      <TableSortLabel
                        active={sortBy === 'fq.name'}
                        direction={getTableSortDirection('fq.name')}
                        onClick={() => handleSort('fq.name')}
                        sx={{
                          color: 'inherit !important',
                          '&:hover': { color: 'inherit !important' },
                          '&.Mui-active': { color: 'inherit !important' },
                          '& .MuiTableSortLabel-icon': { color: 'inherit !important' },
                        }}
                      >
                        Freq.
                      </TableSortLabel>
                    </StyledTableCell>
                    <StyledTableCell dark={true}>
                      <TableSortLabel
                        active={sortBy === 'basePrice'}
                        direction={getTableSortDirection('basePrice')}
                        onClick={() => handleSort('basePrice')}
                        sx={{
                          color: 'inherit !important',
                          '&:hover': { color: 'inherit !important' },
                          '&.Mui-active': { color: 'inherit !important' },
                          '& .MuiTableSortLabel-icon': { color: 'inherit !important' },
                        }}
                      >
                        Base Price
                      </TableSortLabel>
                    </StyledTableCell>
                    <StyledTableCell dark={true}>
                      <TableSortLabel
                        active={sortBy === 'discountRate'}
                        direction={getTableSortDirection('discountRate')}
                        onClick={() => handleSort('discountRate')}
                        sx={{
                          color: 'inherit !important',
                          '&:hover': { color: 'inherit !important' },
                          '&.Mui-active': { color: 'inherit !important' },
                          '& .MuiTableSortLabel-icon': { color: 'inherit !important' },
                        }}
                      >
                        Discount Rate
                      </TableSortLabel>
                    </StyledTableCell>
                    <StyledTableCell dark={true}>
                      <TableSortLabel
                        active={sortBy === 'startDate'}
                        direction={getTableSortDirection('startDate')}
                        onClick={() => handleSort('startDate')}
                        sx={{
                          color: 'inherit !important',
                          '&:hover': { color: 'inherit !important' },
                          '&.Mui-active': { color: 'inherit !important' },
                          '& .MuiTableSortLabel-icon': { color: 'inherit !important' },
                        }}
                      >
                        Start
                      </TableSortLabel>
                    </StyledTableCell>
                    <StyledTableCell dark={true}>
                      <TableSortLabel
                        active={sortBy === 'expiryDate'}
                        direction={getTableSortDirection('expiryDate')}
                        onClick={() => handleSort('expiryDate')}
                        sx={{
                          color: 'inherit !important',
                          '&:hover': { color: 'inherit !important' },
                          '&.Mui-active': { color: 'inherit !important' },
                          '& .MuiTableSortLabel-icon': { color: 'inherit !important' },
                        }}
                      >
                        Expiry
                      </TableSortLabel>
                    </StyledTableCell>
                    <StyledTableCell dark={true} style={{ textAlign: 'center' }}>
                      Status
                    </StyledTableCell>
                    <StyledTableCell dark={true}>
                      <TableSortLabel
                        active={sortBy === 'rateExpiryDate'}
                        direction={getTableSortDirection('rateExpiryDate')}
                        onClick={() => handleSort('rateExpiryDate')}
                        sx={{
                          color: 'inherit !important',
                          '&:hover': { color: 'inherit !important' },
                          '&.Mui-active': { color: 'inherit !important' },
                          '& .MuiTableSortLabel-icon': { color: 'inherit !important' },
                        }}
                      >
                        Rate Expiry
                      </TableSortLabel>
                    </StyledTableCell>
                    <StyledTableCell dark={true}>
                      <TableSortLabel
                        active={sortBy === 'u.firstName'}
                        direction={getTableSortDirection('u.firstName')}
                        onClick={() => handleSort('u.firstName')}
                        sx={{
                          color: 'inherit !important',
                          '&:hover': { color: 'inherit !important' },
                          '&.Mui-active': { color: 'inherit !important' },
                          '& .MuiTableSortLabel-icon': { color: 'inherit !important' },
                        }}
                      >
                        Sales Rep
                      </TableSortLabel>
                    </StyledTableCell>
                    <StyledTableCell dark={true}>&nbsp;</StyledTableCell>
                  </StyledTableRow>
                </TableHead>
                <TableBody>
                  {(subscriptions ?? []).map(
                    (s) => <SubscriptionListRow subscription={s} key={s.id} />,
                  )}
                </TableBody>
              </MuiTable>
            )}
          </TableContainer>
        </>
      )}

      <Stack
        direction="row"
        spacing={1}
        sx={{
          position: 'fixed',
          top: 84,
          right: 24,
          zIndex: 4000,
        }}
      >
        <Tooltip title="Go to superlift" arrow>
          <span>
            <Fab
              color="primary"
              aria-label="link to superlift"
              onClick={() => {
                window.open(
                  `https://superlift.theliftsystem.com/subscriptions/index`,
                  '_blank',
                );
              }}
            >
              <LinkIcon />
            </Fab>
          </span>
        </Tooltip>
        <Tooltip title="Create a new subscription" arrow>
          <span>
            <Fab
              color="primary"
              aria-label="create new sub"
              onClick={() => window.open(
                `/subscriptions/form`,
                '_blank',
              )}
            >
              <AddIcon />
            </Fab>
          </span>
        </Tooltip>
      </Stack>
    </>
  );
};
